import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './InlineSaveRescueBanner.module.css';

/**
 * How a Discard ended, decided by the caller from the offer selection once
 * the discard settled: 'kept' when the recording it was for is still offered
 * (the discard failed, rejected or had no answer), 'settled' otherwise (it
 * was removed, or it is no longer this page's to discard).
 */
export type RescueDiscardOutcome = 'kept' | 'settled';

interface InlineSaveRescueBannerProps {
  /** Resolves once the discard settles; a tap while it is pending is ignored. */
  onDiscard: () => Promise<RescueDiscardOutcome>;
  parked?: boolean;
  /**
   * RU item 2 (2026-10-20): the Bible passage of the offered recording (made
   * on Pray on Scripture), named so the person knows which recording Save
   * keeps as a prayer on that passage. Null for a recording with no passage.
   */
  scriptureReference?: string | null;
  /**
   * Mic repair F2 (2026-10-20): when the offered recording was made, already
   * formatted for the reader (formatShortDateTime), so the banner names the
   * recording it means and reads apart from a prayer just saved.
   */
  capturedAtLabel?: string | null;
  /**
   * Mic repair F2: no words were heard in the offered recording
   * (audio-queue.ts inlineSaveOfferHeardNoWords). The banner says so and the
   * page offers no empty Save for terminal failures; Discard stays, and the recording is kept until
   * the person discards it.
   */
  heardNoWords?: boolean;
  /** Terminal without words, including failures whose cause is unknown. */
  failedWithoutWords?: boolean;
  /**
   * Mic repair F2: Save this recording as a prayer, beside its Discard. Given
   * only when it may be saved (never for a terminal failure without words). The offer's Save lives in the banner so Home's dock keeps the
   * Garden button: the two used to share one spot, so either could cover the
   * other (MRDV2, 375x559 and 375x667).
   */
  onSave?: () => void;
}

/**
 * V.B.3 — surfaces the V.1.1 inline-save rescue state on PrayPage.
 * Mounted only when an awaitingInlineSave orphan is parked in IDB AND
 * the user has no typed text AND no active recording (gate composed
 * by PrayPage). Persistent (no auto-dismiss); hides reactively when
 * any of the four gate conditions flips.
 *
 * Informational, not a primary affordance — the existing Save button
 * remains the action path that atomically claims the orphan. Discard is a
 * quiet explicit action only while Enhanced Voice is available; parked audio
 * has no delete-only path while its processing feature is disabled.
 *
 * Honest copy register matching V.B.2's recoveryBanner family: tells
 * the user what happened and what they can do, no false reassurance
 * about audio safety.
 *
 * One Discard at a time (FT10, 2026-10-19), as ResumeRecordingCard's
 * runExclusiveAction: the banner stays while the registry answers, so a
 * second tap used to reach the Discard again, was refused, and hid the offer
 * of a recording the first tap might still keep.
 *
 * A Discard that did not happen (stream C, 2026-10-19): when the caller
 * reports 'kept' (or, defensively, rejects), the recording stays, the banner
 * says so in an alert, and Discard is enabled again for a retry. If focus was
 * on Discard when it was pressed, it returns there once Discard is enabled
 * again (it can drop to the page while the button is disabled), unless the
 * person has moved it elsewhere meanwhile. A rejection is logged, never left
 * unhandled.
 */
export default function InlineSaveRescueBanner({
  onDiscard,
  parked = false,
  scriptureReference = null,
  capturedAtLabel = null,
  heardNoWords = false,
  failedWithoutWords = false,
  onSave,
}: InlineSaveRescueBannerProps) {
  const { t } = useTranslation();
  const [discardInFlight, setDiscardInFlight] = useState(false);
  // State disables the rendered button; the ref closes the smaller same-tick
  // window before React can publish that disabled state.
  const discardInFlightRef = useRef(false);
  // A positive number while the last Discard did not happen; a new number
  // re-inserts the alert so a repeated failure is announced again.
  const [failedNotice, setFailedNotice] = useState(0);
  const failedSeqRef = useRef(0);
  const discardRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef(false);
  const mountedRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Runs after Discard is enabled again (a disabled button cannot take focus).
  useEffect(() => {
    if (discardInFlight || !restoreFocusRef.current) return;
    restoreFocusRef.current = false;
    const button = discardRef.current;
    const active = document.activeElement;
    if (button && (active === null || active === document.body || active === button)) {
      button.focus();
    }
  }, [discardInFlight, failedNotice]);

  const runExclusiveDiscard = async (): Promise<void> => {
    if (discardInFlightRef.current) return;
    discardInFlightRef.current = true;
    const focusWasOnDiscard =
      discardRef.current !== null && document.activeElement === discardRef.current;
    setDiscardInFlight(true);
    setFailedNotice(0);
    let outcome: RescueDiscardOutcome;
    try {
      outcome = await onDiscard();
    } catch (error) {
      // PrayPage resolves every outcome itself; this keeps an unexpected
      // rejection from going unhandled. Nothing says the recording is gone,
      // so the banner reports that the discard did not happen.
      console.error(
        '[Adia] rescue-discard-failed',
        error instanceof Error ? error.name : typeof error,
      );
      outcome = 'kept';
    } finally {
      discardInFlightRef.current = false;
      setDiscardInFlight(false);
    }
    if (!mountedRef.current || outcome !== 'kept') return;
    failedSeqRef.current += 1;
    restoreFocusRef.current = focusWasOnDiscard;
    setFailedNotice(failedSeqRef.current);
  };

  return (
    <div className={styles.banner} role="status" aria-live="polite">
      <div className={styles.content}>
        <p className={styles.title}>
          {scriptureReference
            ? t(heardNoWords ? 'voice.rescueBannerScriptureNoSpeechTitle'
              : failedWithoutWords ? 'voice.rescueBannerScriptureFailedTitle' : 'voice.rescueBannerScriptureTitle', { reference: scriptureReference })
            : t(heardNoWords ? 'voice.rescueBannerNoSpeechTitle'
              : failedWithoutWords ? 'voice.rescueBannerFailedTitle' : 'voice.rescueBannerTitle', { time: capturedAtLabel ?? '' })}
        </p>
        <p className={styles.body}>
          {t(parked
            // Fix round 1: a parked terminal failure offers neither Save nor
            // Discard, so its body names no button and says nothing is needed.
            ? failedWithoutWords ? 'voice.rescueBannerNoSpeechParkedBody'
              : scriptureReference ? 'voice.rescueBannerScriptureParkedBody' : 'voice.rescueBannerParkedBody'
            : failedWithoutWords
              ? 'voice.rescueBannerNoSpeechBody'
              : scriptureReference ? 'voice.rescueBannerScriptureBody' : 'voice.rescueBannerBody')}
        </p>
        {!parked && failedNotice > 0 && (
          <p key={failedNotice} className={styles.failed} role="alert">
            {t('voice.discardFailed')}
          </p>
        )}
      </div>
      {(onSave || !parked) && (
        <div className={styles.actions}>
          {onSave && (
            <button
              type="button"
              className={styles.save}
              disabled={discardInFlight}
              onClick={onSave}
            >
              {t('pray.save')}
            </button>
          )}
          {!parked && (
            <button
              ref={discardRef}
              type="button"
              className={styles.discard}
              disabled={discardInFlight}
              onClick={() => runExclusiveDiscard()}
            >
              {t('voice.rescueBannerDiscard')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
